import { Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useNavigate } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import CustomModal from "../../../components/CustomModal";
import PaginationCustom from "../../../components/Pagination";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/FormSteps.css";
import "../../../styles/Payroll.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import { formats, modules } from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  formatPayrollCurrency,
} from "./Payroll";

const payrollTypeOptions = [
  { label: "Monthly", value: "Monthly" },
  { label: "Bi Monthly", value: "Bi Monthly" },
  { label: "Weekly", value: "Weekly" },
];

const monthOptions = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const stepItems = [
  { title: "Period" },
  { title: "Drivers" },
  { title: "Calculation" },
];

const initialValues = {
  month: "",
  year: "",
  payrollType: "",
  periodFrom: "",
  periodTo: "",
  selectedDriverIds: [],
  notes: "",
};

const stepSchemas = [
  Yup.object().shape({
    month: Yup.number().required("Month is required"),
    year: Yup.number().required("Year is required"),
    payrollType: Yup.string().required("Payroll type is required"),
    periodFrom: Yup.string().required("Period from is required"),
    periodTo: Yup.string().required("Period to is required"),
  }),
  Yup.object().shape({
    selectedDriverIds: Yup.array().min(1, "Select at least one driver"),
  }),
  Yup.object().shape({}),
];

const getSelectedType = (value) =>
  payrollTypeOptions.find((option) => option.value === value) || null;

const formatDisplayDate = (value = "") => {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const CreatePayrollRun = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);

  const [activeDrivers, setActiveDrivers] = useState([]);
  const [activeDriversLoading, setActiveDriversLoading] = useState(false);
  const [driversPage, setDriversPage] = useState(1);
  const [driversTotal, setDriversTotal] = useState(0);
  const [driversLimit, setDriversLimit] = useState(10);
  const [selectedDriverObjects, setSelectedDriverObjects] = useState([]);
  const [driversSearchText, setDriversSearchText] = useState("");
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDriverForAttendance, setSelectedDriverForAttendance] = useState(null);
  const [periodDates, setPeriodDates] = useState({
    periodFrom: "",
    periodTo: "",
  });

  const [payrollSummary, setPayrollSummary] = useState({});
  const [pendingInstallments, setPendingInstallments] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);

  const currentSchema = useMemo(() => stepSchemas[currentStep], [currentStep]);

  // Reset page when search text or limit changes
  useEffect(() => {
    setDriversPage(1);
  }, [driversSearchText, driversLimit]);

  useEffect(() => {
    if (currentStep !== 1) return;

    let isMounted = true;
    const fetchActiveDrivers = async () => {
      setActiveDriversLoading(true);
      try {
        const response = await apiService.getActiveDriversPaginated({
          page: driversPage,
          limit: driversLimit,
          search_text: driversSearchText,
          searchFilters: {
            start_at: periodDates.periodFrom,
            end_at: periodDates.periodTo,
          }
        });
        const pageData = response?.data || response || {};
        const results = Array.isArray(pageData?.results) ? pageData.results : [];
        if (isMounted) {
          setActiveDrivers(results);
          setDriversTotal(Number(pageData?.total ?? pageData?.meta?.totalItems ?? results.length));
        }
      } catch (error) {
        errorNotify(error || "Failed to load active drivers");
      } finally {
        if (isMounted) {
          setActiveDriversLoading(false);
        }
      }
    };

    fetchActiveDrivers();
    return () => {
      isMounted = false;
    };
  }, [currentStep, driversPage, driversLimit, periodDates, driversSearchText]);

  const handleShowAttendance = async (driver) => {
    setSelectedDriverForAttendance(driver);
    setAttendanceModalOpen(true);
    setAttendanceLoading(true);
    setAttendanceData([]);
    try {
      const response = await apiService.getDriverAttendanceDetails({
        driver_id: Number(driver.user_id || driver.user?.id || driver.id),
        start_at: periodDates.periodFrom,
        end_at: periodDates.periodTo,
      });
      const data = response?.data || response || [];
      setAttendanceData(Array.isArray(data) ? data : []);
    } catch (error) {
      errorNotify(error || "Failed to load driver attendance details");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const getDriverCalculation = (driver, month, year) => {
    const driverInfoId = Number(driver.id || driver.driver_info_id_num);
    const summary = payrollSummary[driverInfoId] || {};

    const basicSalary = Number(driver.basic_salary || 0);
    const isMonthly = String(driver.salary_term || "monthly").toLowerCase() === "monthly";
    let totalDays = summary.total_days || 0;
    if (!totalDays && month && year) {
      totalDays = new Date(Number(year), Number(month), 0).getDate();
    }

    const presentDays = Number(summary.present_days ?? 0);
    const absentDays = Number(summary.absent_days ?? 0);
    const leaveDays = Number(summary.leave_days ?? 0);
    const unmarkedDays = Number(summary.unmarked_days ?? (totalDays - presentDays - absentDays - leaveDays));
    const daysWorked = presentDays + leaveDays;

    const divisor = isMonthly ? (totalDays || 30) : 1;
    const dailyRate = basicSalary / divisor;
    const basePay = dailyRate * daysWorked;

    const DEDUCTION_TYPES = ["arrears_take", "deduction"];
    const allBreakdown = Array.isArray(summary.allowance_summary) ? summary.allowance_summary : [];
    const allowanceBreakdown = allBreakdown.filter((a) => !DEDUCTION_TYPES.includes(a.allowance_type));
    const deductionBreakdown = allBreakdown.filter((a) => DEDUCTION_TYPES.includes(a.allowance_type));
    const allowances = allowanceBreakdown.reduce((s, a) => s + Number(a.total_amount || 0), 0);
    const deductions = deductionBreakdown.reduce((s, a) => s + Number(a.total_amount || 0), 0);
    const instData = pendingInstallments[driverInfoId] || {};
    const shortagePenalization = Number(instData.installment_amount ?? 0);

    const netPay = basePay + allowances - deductions - shortagePenalization;

    return {
      id: driver.driver_info_id || `DRV-${driver.id}`,
      driverInfoId,
      userId: Number(driver.user_id || driver.user?.id),
      name: driver.user?.username || `Driver #${driver.id}`,
      basicSalary,
      totalDays,
      presentDays,
      absentDays,
      leaveDays,
      unmarkedDays,
      daysWorked,
      salaryType: driver.salary_term || "monthly",
      basePay,
      allowances,
      allowanceBreakdown,
      deductions,
      deductionBreakdown,
      shortagePenalization,
      netPay,
    };
  };

  const validateCurrentStep = async (values, setTouched) => {
    try {
      await currentSchema.validate(values, { abortEarly: false });
      return true;
    } catch (validationError) {
      const touched = {};
      validationError.inner.forEach((error) => {
        if (error.path) touched[error.path] = true;
      });
      setTouched(touched, true);
      return false;
    }
  };

  const handleSubmit = async (values) => {
    setCreating(true);
    try {
      const monthObj = monthOptions.find((m) => m.value === values.month);
      const monthName = monthObj ? monthObj.label : "";

      const ALLOWANCE_LABEL = { food: "Food", tada: "TA/DA", trip: "Trip Allowance", mobile: "Mobile Allowance", arrears_give: "Arrears (Given)", bonus: "Bonus" };
      const DEDUCTION_LABEL = { arrears_take: "Arrears (Recovery)", deduction: "Deduction" };
      const freqLabel = (a) => a.frequency === "one_time" ? "One-time" : a.frequency === "monthly" ? "Monthly" : `${a.marked_count || 0} days`;

      const driverObjects = selectedDriverObjects.map((d) => {
        const calc = getDriverCalculation(d, values.month, values.year);
        const grossSalary = calc.basePay + calc.allowances;
        const items = [
          ...(calc.allowanceBreakdown || []).map((a) => ({
            item_type: "allowance",
            title: ALLOWANCE_LABEL[a.allowance_type] || a.allowance_type,
            amount: Number(a.total_amount || 0),
            description: freqLabel(a),
          })),
          ...(calc.deductionBreakdown || []).map((a) => ({
            item_type: "deduction",
            title: DEDUCTION_LABEL[a.allowance_type] || a.allowance_type,
            amount: Number(a.total_amount || 0),
            description: freqLabel(a),
          })),
        ];
        return {
          driver_id: Number(calc.userId),
          driver_info_id: Number(calc.driverInfoId),
          basic_salary: Number(calc.basicSalary),
          days_worked: Number(calc.daysWorked),
          absent_days: Number(calc.absentDays),
          leave_days: Number(calc.leaveDays),
          allowances: Number(calc.allowances),
          deductions: Number(calc.deductions),
          shortage_penalty: Number(calc.shortagePenalization),
          gross_salary: Number(grossSalary),
          net_salary: Number(calc.netPay),
          items,
        };
      });

      const payload = {
        month: Number(values.month),
        year: Number(values.year),
        month_name: monthName,
        start_at: values.periodFrom,
        end_at: values.periodTo,
        notes: values.notes ? values.notes.replace(/<[^>]*>/g, '').trim() : "",
        driver_ids: driverObjects,
      };

      const response = await apiService.createDriverPayroll(payload);
      successNotify(response?.message || "Payroll run created successfully");
      navigate("/payroll");
    } catch (error) {
      errorNotify(error || "Failed to create payroll run");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create_vehicle_page trip_cost_estimation_create_page driver_payments_create_page payroll_create_page">
      <Formik
        initialValues={initialValues}
        validationSchema={currentSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: submitForm,
          setFieldValue,
          setFieldTouched,
          setTouched,
          isSubmitting,
        }) => {
          const selectedDrivers = selectedDriverObjects.map((d) =>
            getDriverCalculation(d, values.month, values.year)
          );
          const totalNetPay = selectedDrivers.reduce(
            (sum, driver) => sum + driver.netPay,
            0
          );
          const isLastStep = currentStep === stepItems.length - 1;

          const fetchPayrollSummary = async (driverObjects, from, to) => {
            const driverInfoIds = driverObjects
              .map((d) => Number(d.id || d.driver_info_id_num))
              .filter(Boolean);
            if (!driverInfoIds.length) return;
            setSummaryLoading(true);
            try {
              const [summaryRes, instRes] = await Promise.all([
                apiService.getPayrollAttendanceSummary(driverInfoIds, from, to),
                apiService.getPendingShortageInstallments(driverInfoIds),
              ]);
              const summaryMap = {};
              ((summaryRes?.data) || []).forEach((s) => {
                summaryMap[s.driver_info_id] = s;
              });
              setPayrollSummary(summaryMap);
              const instMap = {};
              ((instRes?.data) || []).forEach((i) => {
                instMap[i.driver_info_id] = i;
              });
              setPendingInstallments(instMap);
            } catch {
              // summary is optional — silently ignore
            } finally {
              setSummaryLoading(false);
            }
          };

          const nextStep = async () => {
            const isValid = await validateCurrentStep(values, setTouched);
            if (isValid) {
              if (currentStep === 0) {
                setPeriodDates({
                  periodFrom: values.periodFrom,
                  periodTo: values.periodTo,
                });
              }
              if (currentStep === 1) {
                await fetchPayrollSummary(
                  selectedDriverObjects,
                  values.periodFrom,
                  values.periodTo,
                );
              }
              setCurrentStep((previous) =>
                Math.min(previous + 1, stepItems.length - 1)
              );
            }
          };

          const previousStep = () => {
            setCurrentStep((previous) => Math.max(previous - 1, 0));
          };

          const gotoStep = async (targetStep) => {
            if (targetStep <= currentStep) {
              setCurrentStep(targetStep);
              return;
            }

            const isValid = await validateCurrentStep(values, setTouched);
            if (isValid) {
              if (currentStep === 0 && targetStep === 1) {
                setPeriodDates({
                  periodFrom: values.periodFrom,
                  periodTo: values.periodTo,
                });
              }
              setCurrentStep(targetStep);
            }
          };

          const saveDraft = () => {
            localStorage.setItem(
              "fms_payroll_create_draft",
              JSON.stringify(values)
            );
            successNotify("Draft saved successfully");
          };

          const toggleDriver = (driver, checked) => {
            const userId = Number(driver.user_id || driver.user?.id);
            const nextDriverIds = checked
              ? [...values.selectedDriverIds, userId]
              : values.selectedDriverIds.filter((id) => id !== userId);
            setFieldValue("selectedDriverIds", nextDriverIds);
            setFieldTouched("selectedDriverIds", true);

            if (checked) {
              if (!selectedDriverObjects.some((d) => Number(d.user_id || d.user?.id) === userId)) {
                setSelectedDriverObjects((prev) => [...prev, driver]);
              }
            } else {
              setSelectedDriverObjects((prev) => prev.filter((d) => Number(d.user_id || d.user?.id) !== userId));
            }
          };

          const isAllPageSelected = activeDrivers.length > 0 && activeDrivers.every((driver) =>
            values.selectedDriverIds.includes(Number(driver.user_id || driver.user?.id))
          );

          const toggleAllPage = (checked) => {
            let nextDriverIds = [...values.selectedDriverIds];
            let nextDriverObjects = [...selectedDriverObjects];

            activeDrivers.forEach((driver) => {
              const userId = Number(driver.user_id || driver.user?.id);
              if (checked) {
                if (!nextDriverIds.includes(userId)) {
                  nextDriverIds.push(userId);
                  nextDriverObjects.push(driver);
                }
              } else {
                nextDriverIds = nextDriverIds.filter((id) => id !== userId);
                nextDriverObjects = nextDriverObjects.filter((d) => Number(d.user_id || d.user?.id) !== userId);
              }
            });

            setFieldValue("selectedDriverIds", nextDriverIds);
            setFieldTouched("selectedDriverIds", true);
            setSelectedDriverObjects(nextDriverObjects);
          };

          const getMonthValueString = () => {
            if (values.month && values.year) {
              return `${values.year}-${String(values.month).padStart(2, "0")}`;
            }
            return "";
          };

          return (
            <form onSubmit={submitForm}>
              <Breadcrumbs values={["Finance", "Payroll", "Create"]} />

              <div className="page_header access_view">
                <h2>Create Payroll Run</h2>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    cancelBtn={() => navigate("/payroll")}
                    shouldRow={false}
                    isShowSubmitBtn={false}
                    cancelBtnText="Cancel"
                  />
                  <Button
                    submitText="Save"
                    submitBtn={submitForm}
                    loading={creating || isSubmitting}
                    disabled={!isLastStep || creating || isSubmitting}
                    shouldRow={false}
                    isShowCancel={false}
                    isShowSubmitBtn={true}
                  />
                </div>
              </div>

              <div className="page_container">
                <Row>
                  <Col md={12} className="payroll_details_col">
                    <div className="trip_information_main_div">
                      <h5>Payroll Details</h5>
                      <div>
                        <h6>
                          Payroll ID: <span>PR-AUTO</span>
                        </h6>
                        <div className="d-flex payroll_customer_like_layout">
                          <div style={{ minWidth: "175px" }}>
                            <Steps
                              className="transporter_steps form_steps_vertical"
                              current={currentStep}
                              direction="vertical"
                              items={stepItems}
                              onChange={gotoStep}
                            />
                          </div>

                          <div className="w-100 mb-3 payroll_step_content">
                            {currentStep === 0 && (
                              <div>
                                <Row>
                                  <Col md={6}>
                                    <Input
                                      label="Month / Year"
                                      type="month"
                                      placeholder="Select Month and Year"
                                      name="month"
                                      value={getMonthValueString()}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) {
                                          const [y, m] = val.split("-");
                                          const monthNum = Number(m);
                                          const yearNum = Number(y);
                                          setFieldValue("month", monthNum);
                                          setFieldValue("year", yearNum);

                                          const formattedMonth = String(monthNum).padStart(2, "0");
                                          const firstDate = `${yearNum}-${formattedMonth}-01`;
                                          const lastDay = new Date(yearNum, monthNum, 0).getDate();
                                          const lastDate = `${yearNum}-${formattedMonth}-${String(lastDay).padStart(2, "0")}`;

                                          setFieldValue("periodFrom", firstDate);
                                          setFieldValue("periodTo", lastDate);
                                        } else {
                                          setFieldValue("month", "");
                                          setFieldValue("year", "");
                                          setFieldValue("periodFrom", "");
                                          setFieldValue("periodTo", "");
                                        }
                                      }}
                                      onBlur={() => {
                                        setFieldTouched("month", true);
                                        setFieldTouched("year", true);
                                      }}
                                      error={touched.month && errors.month ? errors.month : (touched.year && errors.year ? errors.year : null)}
                                      touched={touched.month || touched.year}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Select
                                      label="Payroll Type"
                                      placeholder="Select Type"
                                      options={payrollTypeOptions}
                                      value={getSelectedType(values.payrollType)}
                                      name="payrollType"
                                      onChange={(option) =>
                                        setFieldValue(
                                          "payrollType",
                                          option ? option.value : ""
                                        )
                                      }
                                      onBlur={() => setFieldTouched("payrollType", true)}
                                      error={errors.payrollType}
                                      touched={touched.payrollType}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Period From"
                                      type="date"
                                      name="periodFrom"
                                      value={values.periodFrom}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.periodFrom}
                                      touched={touched.periodFrom}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Period To"
                                      type="date"
                                      name="periodTo"
                                      value={values.periodTo}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.periodTo}
                                      touched={touched.periodTo}
                                    />
                                  </Col>
                                  <Col md={12}>
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="cancel_btn" disabled>
                                        Previous
                                      </button>
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={nextStep}
                                      >
                                        Next
                                      </button>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            )}

                            {currentStep === 1 && (
                              <div>
                                <p className="payroll_step_hint">
                                  Days Worked is derived from attendance for{" "}
                                  <strong>
                                    {values.periodFrom} - {values.periodTo}
                                  </strong>{" "}
                                  (Present days). Click a value to view the attendance report.
                                </p>
                                <div className="bulk_invoice_searchbar mb-3" style={{ position: "relative", border: "1px solid #cbd5e1", borderRadius: "8px", display: "flex", alignItems: "center", padding: "8px 12px", background: "#fff" }}>
                                  <FiSearch style={{ color: "#94a3b8", marginRight: "8px" }} />
                                  <input
                                    type="text"
                                    value={driversSearchText}
                                    onChange={(event) => setDriversSearchText(event.target.value)}
                                    placeholder="Search Drivers"
                                    style={{ border: "none", outline: "none", width: "100%", fontSize: "14px", color: "#334155" }}
                                  />
                                </div>
                                <div className="payroll_plain_table mb-3">
                                  {activeDriversLoading ? (
                                    <div className="text-center py-4">Loading active drivers...</div>
                                  ) : (
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>
                                            <input
                                              type="checkbox"
                                              checked={isAllPageSelected}
                                              onChange={(e) => toggleAllPage(e.target.checked)}
                                            />
                                          </th>
                                          <th>ID</th>
                                          <th>Driver</th>
                                          <th>Base Salary</th>
                                          <th>Days Worked</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {activeDrivers.map((driver) => {
                                          const userId = Number(driver.user_id || driver.user?.id);
                                          const daysWorked = Number(driver.driver_working_days?.count || 0);
                                          return (
                                            <tr key={driver.id}>
                                              <td>
                                                <input
                                                  type="checkbox"
                                                  checked={values.selectedDriverIds.includes(userId)}
                                                  onChange={(event) =>
                                                    toggleDriver(
                                                      driver,
                                                      event.target.checked
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td>{driver.driver_info_id || driver.id}</td>
                                              <td>{driver.user?.username || `Driver #${driver.id}`}</td>
                                              <td>
                                                {driver.basic_salary ? formatPayrollCurrency(driver.basic_salary) : "N/A"}{" "}
                                                {driver.salary_term ? `/ ${driver.salary_term}` : ""}
                                              </td>
                                              <td>
                                                <button
                                                  type="button"
                                                  onClick={() => handleShowAttendance(driver)}
                                                  style={{
                                                    background: "none",
                                                    border: "none",
                                                    color: "#1447b1",
                                                    textDecoration: "none",
                                                    fontWeight: "600",
                                                    padding: 0,
                                                    cursor: "pointer"
                                                  }}
                                                >
                                                  {daysWorked}
                                                </button>
                                              </td>
                                              <td>
                                                <span className="payroll_status_badge is_completed">
                                                  Active
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                                 {!activeDriversLoading && activeDrivers.length > 0 && (
                                   <PaginationCustom
                                     className="inline_pagination"
                                     totalItems={driversTotal}
                                     totalPages={Math.ceil(driversTotal / driversLimit)}
                                     currentPage={driversPage}
                                     setCurrentPage={setDriversPage}
                                     limit={driversLimit}
                                     setLimit={(limit) => {
                                       setDriversLimit(limit);
                                       setDriversPage(1);
                                     }}
                                   />
                                 )}
                                {values.selectedDriverIds.length === 0 &&
                                  touched.selectedDriverIds &&
                                  errors.selectedDriverIds && (
                                    <div className="text-danger mb-3">
                                      {errors.selectedDriverIds}
                                    </div>
                                  )}
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={previousStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}

                            {currentStep === 2 && (
                              <div>
                                {summaryLoading ? (
                                  <div className="text-center py-4" style={{ color: "#64748b" }}>
                                    Loading attendance & allowance summary...
                                  </div>
                                ) : (
                                  <>
                                    <div className="payroll_review_meta mb-3">
                                      <span>
                                        <strong>{monthOptions.find((m) => m.value === values.month)?.label} {values.year}</strong>
                                      </span>
                                      <span>{formatDisplayDate(values.periodFrom)} – {formatDisplayDate(values.periodTo)}</span>
                                      <span>{selectedDrivers.length} Driver(s)</span>
                                    </div>
                                    <div className="payroll_plain_table mb-3" style={{ overflowX: "auto" }}>
                                      <table style={{ minWidth: "1320px", width: "100%" }}>
                                        <thead>
                                          <tr>
                                            <th style={{ minWidth: "130px", textAlign: "left" }}>Driver</th>
                                            <th style={{ minWidth: "80px" }}>Total Days</th>
                                            <th style={{ color: "#047857", minWidth: "70px" }}>Present</th>
                                            <th style={{ color: "#c53030", minWidth: "65px" }}>Absent</th>
                                            <th style={{ color: "#b76e37", minWidth: "75px" }}>On Leave</th>
                                            <th style={{ color: "#64748b", minWidth: "80px" }}>Unmarked</th>
                                            <th className="th_right" style={{ minWidth: "100px" }}>Base Pay</th>
                                            <th className="th_right" style={{ minWidth: "210px" }}>Allowances</th>
                                            <th className="th_right" style={{ color: "#c53030", minWidth: "210px" }}>Deductions</th>
                                            <th className="th_right" style={{ color: "#c53030", minWidth: "140px" }}>Shortage</th>
                                            <th className="th_right" style={{ fontWeight: 700, minWidth: "150px" }}>Net Pay</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {selectedDrivers.map((driver) => (
                                            <tr key={driver.userId}>
                                              <td>
                                                <div style={{ fontWeight: 500 }}>{driver.name}</div>
                                                <div style={{ fontSize: "11px", color: "#94a3b8", textTransform: "capitalize" }}>{driver.salaryType}</div>
                                              </td>
                                              <td>{driver.totalDays}</td>
                                              <td>
                                                <span style={{ color: "#047857", fontWeight: 600 }}>{driver.presentDays}</span>
                                              </td>
                                              <td>
                                                <span style={{ color: "#c53030", fontWeight: 600 }}>{driver.absentDays}</span>
                                              </td>
                                              <td>
                                                <span style={{ color: "#b76e37", fontWeight: 600 }}>{driver.leaveDays}</span>
                                              </td>
                                              <td>
                                                <span style={{ color: "#94a3b8" }}>{driver.unmarkedDays}</span>
                                              </td>
                                              <td style={{ textAlign: "right" }}>{formatPayrollCurrency(driver.basePay)}</td>
                                              <td style={{ textAlign: "right" }}>
                                                <div style={{ fontWeight: 600 }}>{driver.allowances > 0 ? formatPayrollCurrency(driver.allowances) : "—"}</div>
                                                {driver.allowanceBreakdown.length > 0 && (
                                                  <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                                                    {driver.allowanceBreakdown.map((a) => {
                                                      const labelMap = { food: "Food", tada: "TA/DA", trip: "Trip", mobile: "Mobile", arrears_give: "Arrears", bonus: "Bonus" };
                                                      const label = labelMap[a.allowance_type] || a.allowance_type;
                                                      const amtLabel = formatPayrollCurrency(a.total_amount);
                                                      const countLabel = (a.frequency === "monthly" || a.frequency === "one_time") ? a.frequency.replace("_", "-") : `${a.marked_count}d`;
                                                      return (
                                                        <span key={a.allowance_type} style={{ display: "block" }}>
                                                          {label}: {amtLabel} <span style={{ color: "#94a3b8" }}>({countLabel})</span>
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </td>
                                              <td style={{ textAlign: "right" }}>
                                                {driver.deductions > 0 ? (
                                                  <>
                                                    <div style={{ fontWeight: 600, color: "#c53030" }}>- {formatPayrollCurrency(driver.deductions)}</div>
                                                    <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "2px" }}>
                                                      {driver.deductionBreakdown.map((a) => {
                                                        const labelMap = { arrears_take: "Arrears", deduction: "Deduction" };
                                                        const label = labelMap[a.allowance_type] || a.allowance_type;
                                                        const countLabel = (a.frequency === "monthly" || a.frequency === "one_time") ? a.frequency.replace("_", "-") : `${a.marked_count}d`;
                                                        return (
                                                          <span key={a.allowance_type} style={{ display: "block" }}>
                                                            {label}: {formatPayrollCurrency(a.total_amount)} <span style={{ color: "#94a3b8" }}>({countLabel})</span>
                                                          </span>
                                                        );
                                                      })}
                                                    </div>
                                                  </>
                                                ) : <span style={{ color: "#94a3b8" }}>—</span>}
                                              </td>
                                              <td style={{ textAlign: "right", color: driver.shortagePenalization > 0 ? "#c53030" : "#94a3b8" }}>
                                                {driver.shortagePenalization > 0
                                                  ? `- ${formatPayrollCurrency(driver.shortagePenalization)}`
                                                  : "—"}
                                              </td>
                                              <td style={{ textAlign: "right", fontWeight: 700, color: "#1e293b" }}>{formatPayrollCurrency(driver.netPay)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="payroll_total_card mb-3">
                                      <span>Total Net Pay</span>
                                      <strong>{formatPayrollCurrency(totalNetPay)}</strong>
                                    </div>
                                  </>
                                )}
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={previousStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="submit"
                                    className="main_btn"
                                    disabled={creating || isSubmitting || summaryLoading}
                                  >
                                    {creating || isSubmitting ? "Saving..." : "Save Payroll Run"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={12} className="payroll_notes_col" style={{ marginTop: "16px" }}>
                    <div className="trip_notes_main_div">
                      <h5>Notes</h5>
                      <div style={{ padding: "0 12px 12px" }}>
                        <ReactQuill
                          placeholder="Type your notes or additional info..."
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          formats={formats}
                          onChange={(content) => setFieldValue("notes", content)}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <CustomModal
                show={attendanceModalOpen}
                handleClose={() => setAttendanceModalOpen(false)}
                heading="Driver Attendance Details"
                size="lg"
                dialogClassName="driver_attendance_modal_dialog payroll_attendance_modal_dialog"
              >
                <div className="driver_attendance_modal">
                  <div className="mb-3" style={{ fontSize: "13px", color: "#64748b" }}>
                    Showing attendance for <strong>{selectedDriverForAttendance?.user?.username || `Driver #${selectedDriverForAttendance?.id}`}</strong> from <strong>{periodDates.periodFrom}</strong> to <strong>{periodDates.periodTo}</strong>.
                  </div>
                  <div className="driver_attendance_table payroll_attendance_table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: "180px" }}>Date</th>
                          <th style={{ width: "200px" }}>Attendance Status</th>
                          <th>Leave Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceLoading ? (
                          <tr>
                            <td colSpan={3} className="text-center" style={{ padding: "24px", color: "#64748b" }}>
                              Loading attendance records...
                            </td>
                          </tr>
                        ) : attendanceData.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center" style={{ padding: "24px", color: "#64748b" }}>
                              No attendance records found for this period.
                            </td>
                          </tr>
                        ) : (
                          attendanceData.map((item, index) => {
                            const dateStr = item?.attendance_date ? new Date(item.attendance_date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }) : "—";

                            let statusVal = item?.attendance_status || "—";
                            if (statusVal === "OnLeave" || statusVal === "On Leave" || statusVal === "on_leave") {
                              statusVal = "Leave";
                            }

                            return (
                              <tr key={index}>
                                <td>{dateStr}</td>
                                <td>
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      background:
                                        statusVal === "Present"
                                          ? "#ecfdf5"
                                          : statusVal === "Absent"
                                            ? "#fff5f5"
                                            : "#fffbeb",
                                      color:
                                        statusVal === "Present"
                                          ? "#047857"
                                          : statusVal === "Absent"
                                            ? "#c53030"
                                            : "#b76e37ff",
                                      border:
                                        statusVal === "Present"
                                          ? "1px solid #10b981"
                                          : statusVal === "Absent"
                                            ? "1px solid #feb2b2"
                                            : "1px solid #ffde8aff",
                                      textTransform: "capitalize",
                                      fontFamily: "'Roboto', sans-serif",
                                      display: "inline-block",
                                      textAlign: "center",
                                    }}
                                  >
                                    {statusVal}
                                  </span>
                                </td>
                                <td>{item?.leave_reason || "—"}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="driver_attendance_modal_actions d-flex justify-content-end mt-4">
                    <button
                      type="button"
                      className="cancel_btn"
                      onClick={() => setAttendanceModalOpen(false)}
                      style={{ padding: "8px 16px", borderRadius: "6px" }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </CustomModal>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreatePayrollRun;
